import { DEFAULT_WORK_ITEM_PRIORITY, TERMINAL_WORK_ITEM_STATUSES, type DecisionOption, type Message, type WorkItemDetail } from '@deskworks/shared-types';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useParams } from '@tanstack/react-router';
import {
  CalendarClock,
  CheckCircle2,
  ClipboardList,
  Crown,
  ChevronLeft,
  Clock,
  GraduationCap,
  Info,
  MessagesSquare,
  Pause,
  Pencil,
  Play,
  RotateCcw,
  ScrollText,
  ShieldCheck,
  Target,
  XCircle,
} from 'lucide-react';
import { QuestionOptions } from '@/components/decision-options';
import { Confirm } from '@/components/confirm';
import { Markdown } from '@/components/markdown';
import { DefinitionList, MetaItem, Page, PageHeader } from '@/components/page';
import { RelativeTime } from '@/components/relative-time';
import { Section } from '@/components/section';
import { WorkItemStatusBadge } from '@/components/status-badge';
import { ErrorState, LoadingPage } from '@/components/states';
import { TechDetails } from '@/components/tech-details';
import { PendingApprovalCard } from '@/components/work-item/approval-card';
import { isPersonTask, PersonTaskCard } from '@/components/person-task-card';
import { ReportCard } from '@/components/report/report-parts';
import { WaitingActions, WorkItemActions } from '@/components/actions/actions';
import { ArtifactsSection } from '@/components/work-item/artifacts';
import { WorkItemFilesSection } from '@/components/work-item/desk-file-changes';
import { Chat, isAgentRunning } from '@/components/work-item/chat';
import { EditWorkItemDialog } from '@/components/work-item/edit-dialog';
import { useTrainWorkItem } from '@/components/desk/desk-training';
import { AgentPanel, ApprovalsPanel, DependenciesPanel, RelationsPanel } from '@/components/work-item/side-panels';
import { DoneCriteriaSection } from '@/components/work-item/done-criteria';
import { WorkItemTechDetails } from '@/components/work-item/tech';
import { OpenFromResults } from '@/components/work-item/work-log';
import { AgentConversation } from '@/components/work-item/agent-session';
import { RunsSection, TypeAndLinksSection } from '@/components/work-item/runs';
import { CurrentState, inTraining, PlanPanel } from '@/components/work-item/current-state';
import { ProgramPanel } from '@/components/work-item/program';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import { formatAbsolute } from '@/lib/format';
import { useApiMutation, useMe, useWorkspace } from '@/lib/hooks';
import { inSentence, t, tr } from '@/lib/i18n';
import { priorityLabel, REVIEW_DECISION } from '@/lib/labels';
import { qk } from '@/lib/query-keys';
import { useToast } from '@/lib/toast';
import { cn } from '@/lib/utils';
import { useRealtimeEnvelope, useRealtimeStatus, useRealtimeSubscription } from '@/lib/realtime';
import { SessionLink } from '@/components/session-link';

export function WorkItemDetailPage() {
  const { workspaceId, workItemId } = useParams({ from: '/_authed/w/$workspaceId/work-items/$workItemId' });
  const realtime = useRealtimeStatus();
  useRealtimeSubscription(workspaceId, workItemId);

  // Fallback polling while the live connection is down and something is happening.
  const detail = useQuery({
    queryKey: qk.workItemDetail(workItemId),
    queryFn: () => api.workItems.get(workItemId),
    refetchInterval: (q) => (realtime !== 'connected' && q.state.data && isActive(q.state.data) ? 8_000 : false),
  });
  // A sub-item moved on (its run started, it ended): the parent's thread shows it.
  const queryClient = useQueryClient();
  useRealtimeEnvelope((env) => {
    if (env.workItemId && detail.data?.children.some((c) => c.id === env.workItemId)) {
      void queryClient.invalidateQueries({ queryKey: qk.workItemDetail(workItemId) });
    }
  });
  const active = !!detail.data && isActive(detail.data);
  const poll = realtime !== 'connected' && active ? 8_000 : false;
  const activity = useQuery({
    queryKey: qk.workItemActivity(workItemId),
    queryFn: () => api.workItems.activity(workItemId),
    refetchInterval: poll,
  });
  const messages = useQuery({
    queryKey: qk.workItemMessages(workItemId),
    queryFn: () => api.workItems.messages(workItemId),
    refetchInterval: poll,
  });
  const transcript = useQuery({
    queryKey: qk.workItemTranscript(workItemId),
    queryFn: () => api.workItems.transcript(workItemId),
    refetchInterval: poll,
  });
  // Every run with its result: what is open from them stands beside the work history.
  const runs = useQuery({ queryKey: qk.workItemExecutions(workItemId), queryFn: () => api.workItems.executions(workItemId) });

  if (detail.isPending) return <LoadingPage />;
  if (detail.isError) {
    return (
      <Page>
        <BackLink workspaceId={workspaceId} />
        <ErrorState error={detail.error} onRetry={() => void detail.refetch()} />
      </Page>
    );
  }

  const item = detail.data;
  const pendingApproval = item.approvals.find((a) => a.status === 'pending');
  const waitingBar = item.status === 'waiting' && !item.report && !leadHolds(item);

  return (
    <Page wide>
      <BackLink workspaceId={workspaceId} />
      <PageHeader
        actionsOnTop
        eyebrow={
          <>
            <span>{item.itemLabel ?? t('Work Item')}</span>
            {item.parent && (
              <>
                <span aria-hidden>/</span>
                <Link
                  to="/w/$workspaceId/work-items/$workItemId"
                  params={{ workspaceId: item.parent.workspaceId, workItemId: item.parent.id }}
                  className="max-w-64 truncate hover:text-foreground hover:underline"
                >
                  {item.parent.title}
                </Link>
              </>
            )}
          </>
        }
        title={item.title}
        meta={
          <>
            <WorkItemStatusBadge status={item.status} paused={item.paused} />
            {item.kind === 'review' && <ReviewBadge decision={item.reviewDecision} />}
            {item.typeName && (
              <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs font-medium">
                {item.typeName}
                {item.typeStatus ? ` · ${item.typeStatus}` : ''}
              </span>
            )}
            {item.withLead && !TERMINAL_WORK_ITEM_STATUSES.includes(item.status) && (
              <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/15 px-2 py-0.5 text-xs font-medium text-amber-800 dark:text-amber-300" title={t('The Lead decides the next step')}>
                <Crown className="size-3.5" /> {t('With the Lead')}
              </span>
            )}
            {item.originConversationId && <OriginConversation workspaceId={workspaceId} conversationId={item.originConversationId} />}
            {item.frontDesk ? (
              <FrontDeskLink workspaceId={workspaceId} />
            ) : (
              <MetaItem label={t('Desk:')}>
                <Link to="/w/$workspaceId/desks/$deskId" params={{ workspaceId, deskId: item.deskId }} className="hover:text-primary hover:underline">
                  {item.deskName ?? t('Desk')}
                </Link>
              </MetaItem>
            )}
            <MetaItem label={t('Agent:')}>
              {item.assignedAgentId ? (
                <Link
                  to="/w/$workspaceId/agents/$agentId"
                  params={{ workspaceId, agentId: item.assignedAgentId }}
                  className="hover:text-primary hover:underline"
                >
                  {item.assignedAgentName ?? t('Agent')}
                </Link>
              ) : (
                <span className="text-muted-foreground">{t('none')}</span>
              )}
            </MetaItem>
            {item.priority !== DEFAULT_WORK_ITEM_PRIORITY && <MetaItem label={t('Priority:')}>{priorityLabel(item.priority)}</MetaItem>}
            {item.dueAt && (
              <MetaItem label={t('Due:')}>
                <span className={new Date(item.dueAt).getTime() < Date.now() && !TERMINAL_WORK_ITEM_STATUSES.includes(item.status) ? 'font-medium text-red-600' : ''} title={formatAbsolute(item.dueAt)}>
                  <CalendarClock className="mr-1 inline size-3.5" />
                  <RelativeTime value={item.dueAt} />
                </span>
              </MetaItem>
            )}
          </>
        }
        actions={<WorkItemControls item={item} />}
      />

      {item.report && (
        <div className="mb-6">
          <ReportCard item={item} report={item.report} />
        </div>
      )}

      {/* Like a chat with VICKI: the work as it went on the left, everything about it on the right. */}
      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_380px] xl:grid-cols-[minmax(0,1fr)_420px]">
        <div className="min-w-0">
          <Chat
            item={item}
            messages={messages.data}
            activity={activity.data}
            isPending={messages.isPending}
            error={messages.error}
            onRetry={() => void messages.refetch()}
            end={
              <>
                {item.result?.trim() && (
                  <ResultSection result={item.result} goalCheck={item.goalCheck} learned={item.learned} completedAt={item.completedAt} executionId={item.resultExecutionId} />
                )}
                {pendingApproval &&
                  (isPersonTask(pendingApproval) ? (
                    <PersonTaskCard approval={pendingApproval} workspaceId={item.workspaceId} />
                  ) : (
                    <PendingApprovalCard item={item} approval={pendingApproval} />
                  ))}
                {waitingBar && <WaitingBar item={item} messages={messages.data} />}
                <WaitingActions workItemId={item.id} />
              </>
            }
          />
        </div>

        <aside className="flex min-w-0 flex-col gap-6">
          <CurrentState item={item} activity={activity.data} />
          <OpenFromResults item={item} runs={runs.data ?? []} />
          <TaskPanel item={item} />
          <ProgramPanel item={item} />
          {item.kind !== 'report' && <PlanPanel item={item} />}
          <DoneCriteriaSection item={item} />
          <RelationsPanel item={item} />
          <AgentPanel item={item} />
          <DependenciesPanel item={item} />
          <ApprovalsPanel item={item} />
          <TypeAndLinksSection item={item} />
          <ArtifactsSection artifacts={item.artifacts} />
          <WorkItemFilesSection item={item} />
          <WorkItemActions workItemId={item.id} waiting={false} />

          <TechDetails icon={<Info />} title={t('More details')} description={t('Dates, reference, approval rule, every run and the agent’s full session.')}>
            <div className="flex flex-col gap-6">
              <DefinitionList
                items={[
                  { label: t('Created'), value: <RelativeTime value={item.createdAt} /> },
                  ...(item.completedAt ? [{ label: t('Completed'), value: <RelativeTime value={item.completedAt} /> }] : []),
                  { label: t('Priority'), value: priorityLabel(item.priority) },
                  ...(item.externalReference
                    ? [{ label: t('Reference'), value: <span className="font-mono text-xs break-all">{item.externalReference.type}:{item.externalReference.id}</span> }]
                    : []),
                ]}
              />
              {item.approvalRule?.trim() && (
                <Section icon={<ShieldCheck />} title={t('Ask for approval')} description={t('The agent works on its own up to this point, then waits for you.')}>
                  <Markdown text={item.approvalRule} />
                </Section>
              )}
              <RunsSection item={item} />
              <Section
                icon={<ScrollText />}
                title={t('Agent conversation')}
                description={t('The agent’s full session — its instructions, what it said and thought, and every command and tool call with the result.')}
                contentClassName="px-0 pb-0"
              >
                <div className="border-t">
                  <AgentConversation runs={transcript.data} isPending={transcript.isPending} error={transcript.error} onRetry={() => void transcript.refetch()} />
                </div>
              </Section>
            </div>
          </TechDetails>

          <TechDetails>
            <WorkItemTechDetails item={item} events={activity.data} />
          </TechDetails>
        </aside>
      </div>
    </Page>
  );
}

/** What the item is about, beside the work history: the task as given and its goal. */
function TaskPanel({ item }: { item: WorkItemDetail }) {
  const [open, setOpen] = useState(false);
  const description = item.description?.trim();
  const long = (description?.length ?? 0) > 420;
  return (
    <Section icon={<ClipboardList />} title={t('What it is about')}>
      {description ? (
        <>
          <div className={cn('text-sm', long && !open && 'relative max-h-36 overflow-hidden')}>
            <Markdown text={description} />
            {long && !open && <div aria-hidden className="absolute inset-x-0 bottom-0 h-10 bg-gradient-to-t from-card to-transparent" />}
          </div>
          {long && (
            <button type="button" onClick={() => setOpen((o) => !o)} className="mt-1 cursor-pointer text-xs font-medium text-primary hover:underline">
              {open ? t('Show less') : t('Show all')}
            </button>
          )}
        </>
      ) : (
        <p className="text-sm text-muted-foreground">{t('No description — the title is the task.')}</p>
      )}
      {item.goal?.trim() && (
        <div className="mt-3 border-t pt-3">
          <p className="mb-1 flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
            <Target className="size-3.5" /> {t('Goal')}
          </p>
          <Markdown text={item.goal} />
        </div>
      )}
    </Section>
  );
}

/** Waiting for the Lead (a run ended, an agent asked it, sub-items run) rather than for a person — unless the Lead asked a person. */
function leadHolds(item: WorkItemDetail): boolean {
  if (item.waitingReason === 'children') return true;
  return item.withLead && !item.questionOpen;
}

function withPeriod(text: string): string {
  const s = text.trim();
  return /[.!?…:]$/.test(s) ? s : `${s}.`;
}

function isActive(item: WorkItemDetail): boolean {
  return (!TERMINAL_WORK_ITEM_STATUSES.includes(item.status) && !item.paused) || item.followUpRequested || isAgentRunning(item);
}

function BackLink({ workspaceId }: { workspaceId: string }) {
  return (
    <Link
      to="/w/$workspaceId/work-items"
      params={{ workspaceId }}
      className="mb-4 inline-flex items-center gap-1 text-[13px] text-muted-foreground hover:text-foreground"
    >
      <ChevronLeft className="size-4" /> {t('All Work Items')}
    </Link>
  );
}

function ResultSection({
  result,
  goalCheck,
  learned,
  completedAt,
  executionId,
}: {
  result: string;
  goalCheck: string | null;
  learned: string | null;
  completedAt: string | null;
  executionId: string | null;
}) {
  return (
    <Section
      icon={<CheckCircle2 />}
      title={t('Result')}
      className="border-emerald-200 dark:border-emerald-900"
      actions={
        <span className="flex items-center gap-3">
          <SessionLink executionId={executionId} label={t('How this was produced')} />
          {completedAt && (
            <span className="text-xs text-muted-foreground" title={formatAbsolute(completedAt, true)}>
              {tr('finished {time}', { time: <RelativeTime value={completedAt} /> })}
            </span>
          )}
        </span>
      }
    >
      <Markdown text={result} />
      {goalCheck?.trim() && (
        <div className="mt-4 border-t pt-4">
          <p className="mb-2 flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
            <Target className="size-3.5" /> {t('Goal met?')}
          </p>
          <Markdown text={goalCheck} />
        </div>
      )}
      {learned?.trim() && (
        <div className="mt-4 border-t pt-4">
          <p className="mb-2 flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
            <GraduationCap className="size-3.5" /> {t('What the desk learned')}
          </p>
          <Markdown text={learned} />
        </div>
      )}
    </Section>
  );
}

/**
 * What the work waits for from a person, at the end of the work history: the question stands right above in the
 * course, the field below answers it; answers to pick, and "Done – continue" once everything is said.
 */
function WaitingBar({ item, messages }: { item: WorkItemDetail; messages: Message[] | undefined }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const all = messages ?? [];
  // Set by ask_question; otherwise the agent just wrote what it needs as its last message.
  const asked = item.questionOpen;
  const at = asked ? all.findLastIndex((m) => m.senderType === 'agent' && m.metadata?.question === true) : -1;
  const question = at >= 0 ? all[at] : [...all].reverse().find((m) => m.senderType === 'agent');
  // Answers to pick that the question offers (ask_question `options`).
  const options = at >= 0 && Array.isArray(question?.metadata?.options) ? (question.metadata.options as DecisionOption[]) : [];
  const who = question?.senderName ?? item.assignedAgentName ?? t('The agent');
  const done = useApiMutation(() => api.workItems.ready(item.id), {
    invalidate: [qk.workItem(item.id), qk.workItemsAll(item.workspaceId), qk.desk(item.deskId)],
    success: t('The work goes on'),
  });
  return (
    <div className="rounded-xl border-2 border-amber-300 bg-amber-50/70 p-4 dark:border-amber-800 dark:bg-amber-950/30">
      <div className="flex items-start gap-3">
        <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-amber-500/15 text-amber-700 dark:text-amber-300">
          <Clock className="size-4" />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-semibold">{asked ? t('{who} has a question', { who }) : t('The agent is waiting for you')}</h3>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {asked
              ? t('Answer it in the field below, or ask back first — {who} replies here. The work goes on once it is answered.', { who })
              : t('{who} needs something before it can continue. Your answer wakes it up.', { who })}
          </p>
          {!question && <p className="mt-2 text-sm">{withPeriod(item.waitingReason || t('It needs more information from you.'))}</p>}
          {options.length > 0 && (
            <QuestionOptions
              className="mt-3"
              workItemId={item.id}
              options={options}
              onAnswered={() => {
                toast.success(t('The work goes on'));
                void queryClient.invalidateQueries({ queryKey: qk.workItem(item.id) });
                void queryClient.invalidateQueries({ queryKey: qk.workItemMessages(item.id) });
              }}
            />
          )}
          {asked && (
            <div className="mt-3 flex items-center justify-end gap-2">
              <span className="text-xs text-muted-foreground">{t('Everything said?')}</span>
              <Button size="sm" variant="outline" onClick={() => done.mutate()} loading={done.isPending} title={t('{who} goes on with what was said here', { who })}>
                <CheckCircle2 /> {t('Done – continue')}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/** One of VICKI's tasks (docs/features/front-desk.md#tasks): it belongs to the Front Desk, not to a desk to open. */
function FrontDeskLink({ workspaceId }: { workspaceId: string }) {
  const me = useMe();
  const workspace = useWorkspace(workspaceId);
  const account = me.data && workspace.data ? me.data.accounts.find((a) => a.id === workspace.data!.accountId) : undefined;
  return (
    <MetaItem label={t('Task of:')}>
      {account ? (
        <Link to="/$account/front-desk" params={{ account: account.slug || account.id }} search={{}} className="hover:text-primary hover:underline">
          {t('VICKI at the Front Desk')}
        </Link>
      ) : (
        t('VICKI at the Front Desk')
      )}
    </MetaItem>
  );
}

function WorkItemControls({ item }: { item: WorkItemDetail }) {
  const invalidate = [qk.workItem(item.id), qk.workItemsAll(item.workspaceId), qk.desk(item.deskId), qk.overview(item.workspaceId)];
  const terminal = TERMINAL_WORK_ITEM_STATUSES.includes(item.status);

  const pause = useApiMutation(() => api.workItems.pause(item.id), { invalidate, success: t('Paused') });
  const resume = useApiMutation(() => api.workItems.resume(item.id), { invalidate, success: t('Continuing — an agent picks it up shortly.') });
  const cancel = useApiMutation(() => api.workItems.cancel(item.id), { invalidate, success: t('Cancelled') });
  const retry = useApiMutation(() => api.workItems.retry(item.id), { invalidate, success: t('Trying again') });
  const ready = useApiMutation(() => api.workItems.ready(item.id), { invalidate, success: t('Started — an agent will pick it up shortly.') });
  // German capitalises nouns, so there the item's noun keeps its case.
  const noun = item.itemLabel ?? t('Work Item');

  const canStart = (item.status === 'created' || item.status === 'blocked') && !item.paused && !!item.assignedAgentId;
  // Waiting for a person (not for sub-items): continue without writing an answer, e.g. after fixing something yourself.
  const canContinue = item.status === 'waiting' && item.waitingReason !== 'children' && !item.paused;
  // Work it through with the Lead in the desk's training (docs/features/desk-training.md); there it rests until closed.
  const training = inTraining(item);
  const train = useTrainWorkItem({ id: item.deskId, workspaceId: item.workspaceId }, item.lead?.name);
  const canTrain = item.kind === 'task' && !item.frontDesk && !terminal && !training && !isAgentRunning(item);

  return (
    <>
      <EditWorkItemDialog item={item}>
        <Button variant="outline" size="sm">
          <Pencil /> {t('Edit')}
        </Button>
      </EditWorkItemDialog>
      {canStart && (
        <Button size="sm" onClick={() => ready.mutate()} loading={ready.isPending}>
          <Play /> {item.status === 'blocked' ? t('Continue') : t('Start')}
        </Button>
      )}
      {canContinue && (
        <Button size="sm" onClick={() => ready.mutate()} loading={ready.isPending} title={t('Let the agent continue without writing an answer')}>
          <Play /> {t('Continue')}
        </Button>
      )}
      {canTrain && (
        <Button
          variant="outline"
          size="sm"
          onClick={() => train.mutate(item.id)}
          loading={train.isPending}
          title={t('Go through it with the Lead in the training; it learns how such work is done')}
        >
          <GraduationCap /> {t('Work through in training')}
        </Button>
      )}
      {item.status === 'failed' && (
        <Button size="sm" onClick={() => retry.mutate()} loading={retry.isPending}>
          <RotateCcw /> {t('Try again')}
        </Button>
      )}
      {!terminal &&
        !training &&
        (item.paused ? (
          <Button size="sm" onClick={() => resume.mutate()} loading={resume.isPending} title={t('A fresh worker continues from the saved state')}>
            <Play /> {t('Continue')}
          </Button>
        ) : (
          <Button variant="outline" size="sm" onClick={() => pause.mutate()} loading={pause.isPending} title={t('Stop the agent now; nothing is lost')}>
            <Pause /> {t('Pause')}
          </Button>
        ))}
      {!terminal && (
        <Confirm
          title={t('Cancel this {item}?', { item: inSentence(noun) })}
          description={t('The agent stops working on it. Activity and messages so far are kept.')}
          confirmLabel={t('Yes, cancel')}
          cancelLabel={t('No, keep working')}
          destructive
          onConfirm={() => cancel.mutateAsync()}
        >
          <Button variant="outline" size="sm" className="text-destructive hover:text-destructive">
            <XCircle /> {t('Cancel')}
          </Button>
        </Confirm>
      )}
    </>
  );
}

function ReviewBadge({ decision }: { decision: WorkItemDetail['reviewDecision'] }) {
  const tone =
    decision === 'approved'
      ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300'
      : decision === 'changes_requested'
        ? 'bg-amber-500/15 text-amber-800 dark:text-amber-300'
        : 'bg-muted text-muted-foreground';
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${tone}`}>
      <ShieldCheck className="size-3.5" /> {t('Review')}{decision ? ` · ${REVIEW_DECISION[decision]}` : ''}
    </span>
  );
}

/** "From conversation: <title>" — the conversation in which the agent accepted this work. */
function OriginConversation({ workspaceId, conversationId }: { workspaceId: string; conversationId: string }) {
  const conv = useQuery({
    queryKey: qk.conversation(conversationId),
    queryFn: () => api.conversations.get(conversationId),
    staleTime: 60_000,
    retry: false,
  });
  return (
    <MetaItem label={t('From conversation:')}>
      <Link
        to="/w/$workspaceId/conversations/$conversationId"
        params={{ workspaceId, conversationId }}
        className="inline-flex items-center gap-1 hover:text-primary hover:underline"
      >
        <MessagesSquare className="size-3.5" />
        {conv.data?.title || (conv.isError ? t('Open conversation') : '…')}
      </Link>
    </MetaItem>
  );
}
